import React, { lazy, Suspense, useEffect, useState } from 'react';
import { TabSkeleton } from './shared/TabSkeleton';
import { ExtendedProject } from '@/types/project';

// Lazy load all tab components
const ProjectOverviewTabContent = lazy(() => 
  import('./ProjectOverviewTabContent').then(module => ({
    default: module.ProjectOverviewTabContent
  }))
);

// Import already extracted components
const CommunicationTab = lazy(() =>
  import('./CommunicationTab').then(module => ({
    default: module.CommunicationTab
  }))
);

const SmsTab = lazy(() =>
  import('./SmsTab').then(module => ({
    default: module.SmsTab
  }))
);

const ProjectNotesTab = lazy(() =>
  import('./ProjectNotesTab')
);

const PartnerInfoTab = lazy(() => 
  import('./PartnerInfoTab').then(module => ({
    default: module.PartnerInfoTab
  }))
);

const SalesManagerInfoTab = lazy(() => 
  import('./SalesManagerInfoTab').then(module => ({
    default: module.SalesManagerInfoTab
  }))
);

// FIN-1.8: config-driven Finance section replaces the 3 hardcoded finance tabs
// (FinanceTab / CommercialFinanceTab / SalesManagerFinanceTab), routed by
// pipelines.pipeline_type instead of pipeline_id IN (1,57).
const FinanceSection = lazy(() =>
  import('@/components/project/finance/FinanceSection').then(module => ({
    default: module.FinanceSection
  }))
);

const ProjectManagementTab = lazy(() => 
  import('./ProjectManagementTab').then(module => ({
    default: module.ProjectManagementTab
  }))
);

const SiteSurveyTab = lazy(() => 
  import('./SiteSurveyTab').then(module => ({
    default: module.SiteSurveyTab
  }))
);

const BookingConsultationTab = lazy(() => 
  import('./BookingConsultationTab').then(module => ({
    default: module.BookingConsultationTab
  }))
);


const CommercialDesignRequestTab = lazy(() => 
  import('./CommercialDesignRequestTab').then(module => ({
    default: module.CommercialDesignRequestTab
  }))
);

const NewProjectFormTab = lazy(() => 
  import('./NewProjectFormTab').then(module => ({
    default: module.NewProjectFormTab
  }))
);

const WorkOrdersTab = lazy(() => 
  import('./WorkOrdersTab').then(module => ({
    default: module.WorkOrdersTab
  }))
);

const ContractsTab = lazy(() =>
  import('./ContractsTab')
);

const ProposalsTab = lazy(() =>
  import('./ProposalsTab')
);

const LenderTab = lazy(() => 
  import('./LenderTab')
);

const DocumentsTabV2 = lazy(() =>
  import('./DocumentsTabV2').then(module => ({
    default: module.DocumentsTabV2
  }))
);


const ActivityTab = lazy(() => 
  import('./ActivityTab').then(module => ({
    default: module.ActivityTab
  }))
);

const ProjectTasksTab = lazy(() =>
  import('./ProjectTasksTab').then(module => ({
    default: module.ProjectTasksTab
  }))
);

const EmailTab = lazy(() =>
  import('@/components/email/EmailTab').then(module => ({
    default: module.EmailTab
  }))
);

const ReportsTab = lazy(() =>
  import('@/components/reports/ReportsTab').then(module => ({
    default: module.ReportsTab
  }))
);

interface TabManagerProps {
  activeTab: string;
  tabLoaded: Record<string, boolean>;
  project: ExtendedProject | null;
  userRoleId?: number | null;
  [key: string]: any; // For passing through other props
}

export function TabManager({
  activeTab,
  tabLoaded,
  project,
  userRoleId,
  ...props
}: TabManagerProps) {
  const [preloadedTabs, setPreloadedTabs] = useState<Set<string>>(new Set());
  
  // Preload next likely tabs based on current tab
  useEffect(() => {
    const preloadStrategies: Record<string, string[]> = {
      'opportunity': ['communication', 'finance', 'documents'],
      'design': ['documents', 'finance'],
      'finance': ['contracts', 'documents'],
      'communication': ['sms', 'emails', 'notes', 'timeline'],
      'sms': ['communication', 'emails'],
      'emails': ['communication', 'notes'],
      'tasks': ['project-tasks', 'timeline']
    };
    
    const tabsToPreload = preloadStrategies[activeTab] || [];
    
    tabsToPreload.forEach((tab, index) => {
      if (!preloadedTabs.has(tab)) {
        setTimeout(() => {
          // Dynamically import the tab component
          switch(tab) {
            case 'communication':
              import('./CommunicationTab');
              break;
            case 'sms':
              import('./SmsTab');
              break;
            case 'emails':
              import('@/components/email/EmailTab');
              break;
            case 'reports':
              import('@/components/reports/ReportsTab');
              break;
            case 'finance':
              import('@/components/project/finance/FinanceSection');
              break;
            case 'documents':
              import('./DocumentsTabV2');
              break;
            case 'notes':
              import('./ProjectNotesTab');
              break;
            case 'timeline':
              import('./ActivityTab');
              break;
            case 'contracts':
              import('./ContractsTab');
              break;
            case 'proposals':
              import('./ProposalsTab');
              break;
            case 'project-tasks':
              import('./ProjectTasksTab');
              break;
          }
          setPreloadedTabs(prev => new Set(prev).add(tab));
        }, 1500 + (index * 500)); // Stagger preloading
      }
    });
  }, [activeTab, preloadedTabs]);
  
  // Render the active tab with Suspense
  const renderTab = () => {
    if (!tabLoaded[activeTab]) {
      return <TabSkeleton />;
    }
    
    switch (activeTab) {
      case 'opportunity':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectOverviewTabContent
              project={project}
              projectId={props.projectId}
              formValues={props.formValues}
              canEditProject={props.canEditProject}
              loading={props.loading}
              handleInputChange={props.handleInputChange}
              handleSelectChange={props.handleSelectChange}
              handleArrayChange={props.handleArrayChange}
              setFormValues={props.setFormValues}
              handleProjectUpdate={props.handleProjectUpdate}
              handleClose={props.handleClose}
              handleDelete={props.handleDelete}
              onSave={props.onSave}
            />
          </Suspense>
        );
        
      case 'new-project-form':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <NewProjectFormTab 
              formData={props.formData}
              updateFormData={props.updateFormData}
              handleFileChange={props.handleFileChange}
              errors={props.errors}
              pipelineId={props.pipelineId}
            />
          </Suspense>
        );
        
      case 'design':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <DocumentsTabV2
              filePreviewUrls={props.filePreviewUrls}
              fileLoading={props.fileLoading}
              uploadStatus={props.uploadStatus}
              documents={props.documents}
              documentMetadata={props.documentMetadata}
              dragActive={props.dragActive}
              dlVerificationStatus={props.dlVerificationStatus}
              dlVerificationData={props.dlVerificationData}
              ubVerificationStatus={props.ubVerificationStatus}
              ubVerificationData={props.ubVerificationData}
              isVerifying={props.isVerifying}
              isVerifyingUb={props.isVerifyingUb}
              handleFileChange={props.handleFileChange}
              handleDrag={props.handleDrag}
              handleDrop={props.handleDrop}
              handleRemoveFile={props.handleRemoveFile}
              handleVerifyDriversLicense={props.handleVerifyDriversLicense}
              handleVerifyUtilityBill={props.handleVerifyUtilityBill}
              renderFilePreview={props.renderFilePreview}
              userRoleId={userRoleId}
              project={project}
              // Lazy loading props
              documentCounts={props.documentCounts}
              countsLoading={props.countsLoading}
              loadedFields={props.loadedFields}
              fetchFieldDocuments={props.fetchFieldDocuments}
            />
          </Suspense>
        );
        
      case 'commercial-designs':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <CommercialDesignRequestTab 
              formValues={props.formValues}
              onTaskSubmitted={props.onTaskSubmitted}
              isOpen={props.isOpen}
            />
          </Suspense>
        );
        
      case 'book-consultation':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <BookingConsultationTab 
              projectId={props.projectId}
              project={project}
            />
          </Suspense>
        );
        
      case 'contracts':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ContractsTab project={project!} />
          </Suspense>
        );

      case 'proposals':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProposalsTab project={project!} />
          </Suspense>
        );

      case 'lender':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <LenderTab project={project!} />
          </Suspense>
        );
        
      case 'site-survey':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SiteSurveyTab project={project!} />
          </Suspense>
        );
        
      case 'tasks':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectManagementTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'finance':
        if (!project) return null;
        // FIN-1.8 (R6): config-driven section resolved by pipelines.pipeline_type,
        // no longer branching on pipeline_id IN (1,57).
        return (
          <Suspense fallback={<TabSkeleton />}>
            <FinanceSection
              project={project}
              formValues={props.formValues}
              onFieldChange={(name, value) => props.handleInputChange({ name, value })}
            />
          </Suspense>
        );

      case 'communication':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <CommunicationTab project={project!} defaultCommentTab={props.defaultCommentTab} />
          </Suspense>
        );

      case 'sms':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SmsTab project={project!} />
          </Suspense>
        );

      case 'emails':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <EmailTab projectId={project?.id || ''} />
          </Suspense>
        );

      case 'reports':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ReportsTab
              projectId={project?.id || ''}
              pipelineId={project?.pipeline_id || ''}
            />
          </Suspense>
        );

      case 'notes':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectNotesTab project={project!} />
          </Suspense>
        );
        
      case 'partner':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <PartnerInfoTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'sales-manager':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <SalesManagerInfoTab 
              project={project!}
              onProjectUpdate={props.onProjectUpdate}
            />
          </Suspense>
        );
        
      case 'work-orders':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <WorkOrdersTab 
              project={project!}
              currentUserId={props.currentUserId || ''}
            />
          </Suspense>
        );
        
      case 'project-tasks':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ProjectTasksTab 
              openTasks={props.openTasks}
              completedTasks={props.completedTasks}
              assigneeNames={props.assigneeNames}
              showCompletedTasks={props.showCompletedTasks}
              setShowCompletedTasks={props.setShowCompletedTasks}
              setShowTaskForm={props.setShowTaskForm}
              setSelectedTask={props.setSelectedTask}
              currentUserId={props.currentUserId}
            />
          </Suspense>
        );
        
      case 'timeline':
        return (
          <Suspense fallback={<TabSkeleton />}>
            <ActivityTab project={project!} />
          </Suspense>
        );
        
      case 'sales-manager-finance':
        if (!project) return null;
        // FIN-1.8: the Sales Manager view is now part of the config-driven section.
        return (
          <Suspense fallback={<TabSkeleton />}>
            <FinanceSection
              project={project}
              formValues={props.formValues}
              onFieldChange={(name, value) => props.handleInputChange({ name, value })}
              initialViewKey="sales_manager"
            />
          </Suspense>
        );
        
      default:
        return null;
    }
  };
  
  return renderTab();
}